import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { AppLogo, TAGLIE, blurSuTouch } from "@/components/Shared";
import { formattaNome } from "@/lib/utils";
import InformativaBox from "@/components/InformativaBox";

/**
 * Registrazione autonoma di animatori e aiuti.
 * Il link è unico e si condivide nel gruppo: l'accesso al modulo è protetto
 * dal codice stagione, ma il controllo vero è l'approvazione del responsabile,
 * che riconosce la persona. Finché non approva non esiste alcun account.
 */
export default function RegistrazioneStaff() {
  const [stato, setStato] = useState(null);
  const [inviata, setInviata] = useState(false);
  const [messaggio, setMessaggio] = useState("");
  const [saving, setSaving] = useState(false);
  const [attesa, setAttesa] = useState(0);
  const [errore, setErrore] = useState("");

  const [form, setForm] = useState({
    codice_stagione: "", nome: "", cognome: "", data_nascita: "",
    username: "", telefono: "", sesso: "", taglia_maglia: "", password: "", conferma: "",
    informativa_presa_visione: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Età alla registrazione: decide quale informativa mostrare (quella per
  // adulti o quella in linguaggio semplice) e se avvisare che serve la firma
  // di un genitore. Il server ricalcola e non si fida di questo valore.
  const eta = (() => {
    if (!form.data_nascita) return null;
    const d = new Date(form.data_nascita);
    if (isNaN(d)) return null;
    const oggi = new Date();
    let a = oggi.getFullYear() - d.getFullYear();
    const m = oggi.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && oggi.getDate() < d.getDate())) a--;
    return a;
  })();
  const minorenne = eta !== null && eta < 18;

  useEffect(() => {
    api.get("/public/registrazione-staff/stato")
      .then((r) => setStato(r.data))
      .catch(() => setStato({ aperte: false }));
  }, []);

  useEffect(() => {
    if (attesa <= 0) return;
    const t = setInterval(() => setAttesa((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [attesa > 0]);

  const invia = async (e) => {
    e.preventDefault();
    setErrore("");
    if (attesa > 0) return;
    if (!/^[a-z][a-z0-9._-]{2,23}$/.test(form.username))
      return setErrore("Il nome utente: da 3 a 24 caratteri, solo minuscole, numeri, punto, trattino e underscore, e deve iniziare con una lettera");
    if (form.password.length < 8) return setErrore("La password deve avere almeno 8 caratteri");
    if (form.password !== form.conferma) return setErrore("Le password non coincidono");
    if (form.telefono.replace(/\D/g, "").length < 8) return setErrore("Inserisci un numero di cellulare valido");
    if (!form.sesso) return setErrore("Indica il sesso");
    if (!form.taglia_maglia) return setErrore("Scegli la taglia della maglia");
    if (!form.informativa_presa_visione)
      return setErrore("Devi dichiarare di aver letto l'informativa sul trattamento dei dati");

    setSaving(true);
    try {
      const { conferma, ...payload } = form;
      const { data } = await api.post("/public/registrazione-staff", payload);
      setMessaggio(data?.messaggio || "");
      setInviata(true);
    } catch (err) {
      if (err.response?.status === 429) setAttesa(err.retryAfter || 3600);
      setErrore(errMsg(err));
      toast.error(errMsg(err));
    } finally { setSaving(false); }
  };

  const attesaLeggibile = (s) => (s > 60 ? `${Math.ceil(s / 60)} minuti` : `${s} secondi`);

  return (
    <div className="min-h-dvh bg-[#FFFDF9] py-8 px-4">
      <div className="max-w-xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center mb-6">
          <AppLogo size={64} className="mb-3 drop-shadow-lg" />
          <h1 className="font-display text-2xl font-extrabold text-slate-900 text-center">SS. Medici Oratorio Estivo</h1>
          <p className="text-sm font-semibold text-slate-500">Registrazione animatori</p>
        </motion.div>

        {!stato ? (
          <p className="text-center text-slate-400 font-semibold">Caricamento...</p>
        ) : inviata ? (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="card-app p-10 text-center" data-testid="registrazione-inviata">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
            <h2 className="font-display text-xl font-extrabold text-slate-900 mb-2">Richiesta inviata!</h2>
            <p className="text-sm text-slate-500 font-semibold">{messaggio}</p>
          </motion.div>
        ) : !stato.aperte ? (
          <div className="card-app p-10 text-center" data-testid="registrazioni-chiuse">
            <XCircle className="h-14 w-14 text-red-400 mx-auto mb-4" />
            <h2 className="font-display text-xl font-extrabold text-slate-900 mb-2">Registrazioni chiuse</h2>
            <p className="text-sm text-slate-500 font-semibold">Chiedi a un responsabile quando riapriranno.</p>
          </div>
        ) : (
          <form onSubmit={invia} className="card-app p-6" data-testid="registrazione-staff-form">
            <div className="flex items-start gap-2 rounded-2xl bg-amber-50 border border-amber-200 p-3 mb-5">
              <KeyRound className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs font-semibold text-amber-800">
                Ti serve il <b>codice della stagione</b> che i responsabili hanno condiviso nel gruppo.
                Dopo l'invio, un responsabile approverà la tua richiesta.
              </p>
            </div>

            <label className="label-app">Codice della stagione</label>
            <input className="input-app mb-4 uppercase tracking-widest font-mono" required value={form.codice_stagione}
              onChange={(e) => set("codice_stagione", e.target.value.toUpperCase())}
              autoCapitalize="characters" autoCorrect="off" spellCheck="false" data-testid="reg-codice-input" />

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-app">Nome</label>
                <input className="input-app mb-4" required value={form.nome}
                  onChange={(e) => set("nome", e.target.value)}
                  onBlur={(e) => set("nome", formattaNome(e.target.value))} data-testid="reg-nome-input" />
              </div>
              <div>
                <label className="label-app">Cognome</label>
                <input className="input-app mb-4" required value={form.cognome}
                  onChange={(e) => set("cognome", e.target.value)}
                  onBlur={(e) => set("cognome", formattaNome(e.target.value))} data-testid="reg-cognome-input" />
              </div>
            </div>

            <label className="label-app">Nome utente</label>
            <input className="input-app" required value={form.username}
              onChange={(e) => set("username", e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ""))}
              placeholder="mario.rossi" autoCapitalize="none" autoCorrect="off" spellCheck="false"
              maxLength={24} data-testid="reg-username-input" />
            <p className="text-[11px] text-slate-400 font-semibold mt-1 mb-4">
              È il nome con cui accederai. Da 3 a 24 caratteri: minuscole, numeri, punto,
              trattino e underscore. Puoi entrare anche con il numero di cellulare.
              {!form.username && form.nome && form.cognome && (
                <>
                  {" "}
                  <button type="button" className="font-bold text-sky-600 underline underline-offset-2"
                    onClick={() => set("username", `${form.nome}.${form.cognome}`.toLowerCase()
                      .normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9._-]/g, "").slice(0, 24))}
                    data-testid="reg-username-suggerisci">
                    usa {`${form.nome}.${form.cognome}`.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9._-]/g, "").slice(0, 24)}
                  </button>
                </>
              )}
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-app">Data di nascita</label>
                <input type="date" className="input-app mb-4" required value={form.data_nascita}
                  onChange={(e) => set("data_nascita", e.target.value)} data-testid="reg-nascita-input" />
              </div>
              <div>
                <label className="label-app">Sesso</label>
                <select className="input-app mb-4" required value={form.sesso}
                  onChange={(e) => { set("sesso", e.target.value); blurSuTouch(e); }} data-testid="reg-sesso-select">
                  <option value="">Seleziona...</option>
                  <option value="M">Maschile (M)</option>
                  <option value="F">Femminile (F)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-app">Cellulare</label>
                <input type="tel" className="input-app mb-1" required value={form.telefono}
                  onChange={(e) => set("telefono", e.target.value)} placeholder="Es. 333 1234567" data-testid="reg-telefono-input" />
              </div>
              <div>
                <label className="label-app">Taglia maglia</label>
                <select className="input-app mb-1" required value={form.taglia_maglia}
                  onChange={(e) => { set("taglia_maglia", e.target.value); blurSuTouch(e); }} data-testid="reg-taglia-select">
                  <option value="">Seleziona...</option>
                  {TAGLIE.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <p className="text-xs text-slate-400 mb-4">Con questo numero potrai anche accedere all'app.</p>

            <label className="label-app">Scegli una password <span className="text-slate-400 font-normal normal-case">(almeno 8 caratteri)</span></label>
            <input type="password" className="input-app mb-4" required value={form.password}
              onChange={(e) => set("password", e.target.value)} data-testid="reg-password-input" />
            <label className="label-app">Conferma password</label>
            <input type="password" className="input-app mb-4" required value={form.conferma}
              onChange={(e) => set("conferma", e.target.value)} data-testid="reg-conferma-input" />

            {errore && <p className="badge-neg block w-full text-center py-2 mb-4 !text-sm" data-testid="reg-errore">{errore}</p>}
            {/* Il modulo non aveva alcun riferimento alla privacy: né link, né
                casella, né testo. Quello degli iscritti sì. Ai minorenni si
                mostra la versione in linguaggio semplice. */}
            <InformativaBox
              tipo={minorenne ? "staff_minori" : "staff"}
              valore={form.informativa_presa_visione}
              etichetta={minorenne
                ? "Ho letto e capito come vengono usati i miei dati."
                : "Dichiaro di aver letto l'informativa sul trattamento dei miei dati."}
              onChange={({ presaVisione }) => set("informativa_presa_visione", presaVisione)}
              testId="informativa-staff"
            />

            {minorenne && (
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mb-4" data-testid="avviso-minorenne">
                <p className="text-sm font-bold text-slate-800 mb-1">Hai meno di 18 anni</p>
                <p className="text-xs text-slate-700 font-medium leading-relaxed">
                  Puoi registrarti, ma il tuo accesso non funzionerà subito: prima un tuo
                  genitore — o chi si occupa di te — deve firmare un'autorizzazione e
                  consegnarla a un responsabile. Non è un problema tuo: per i dati di un
                  minorenne la legge chiede il consenso di un adulto.
                </p>
                <p className="text-xs text-slate-700 font-medium leading-relaxed mt-2">
                  Fai leggere l'informativa anche a un tuo genitore:{" "}
                  <a href="/informativa/staff" target="_blank" rel="noopener noreferrer"
                    className="font-bold text-amber-800 underline underline-offset-2"
                    data-testid="link-informativa-genitore">
                    versione completa per i genitori
                  </a>.
                </p>
              </div>
            )}

            {attesa > 0 && (
              <p className="text-xs font-bold text-red-600 text-center mb-3" data-testid="reg-attesa">
                Troppi invii da questa connessione. Riprova tra {attesaLeggibile(attesa)}.
              </p>
            )}

            <button className="btn-primary w-full !py-3"
              disabled={saving || attesa > 0 || !form.informativa_presa_visione}
              data-testid="reg-submit-btn">
              {attesa > 0 ? `Attendi ${attesaLeggibile(attesa)}` : saving ? "Invio..." : "Invia richiesta"}
            </button>
            <p className="text-[11px] text-slate-400 text-center mt-4">
              I tuoi dati servono solo all'organizzazione delle attività dell'oratorio.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
